import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO, { SITE_URL } from "@/components/SEO";
import { markPrerenderReady } from "@/lib/seo";
import { trackEvent } from "@/blog/track";
import { FEATURES } from "@/features";

const STEPS = [
  {
    title: "A customer reaches out",
    text: "They message you, comment a keyword on a post or Reel, reply to your Story, or open your Storefront link from anywhere on the internet.",
  },
  {
    title: "They browse and choose",
    text: "Your catalog appears inside the chat, with photos, prices and sizes. They pick a size and quantity.",
  },
  {
    title: "They enter their details",
    text: "Checkout DM collects the name, phone number and delivery address one step at a time.",
  },
  {
    title: "They pay in the chat",
    text: "Through a payment link or manual UPI, QR code or bank transfer. Your payments go to your own account.",
  },
  {
    title: "You ship, the buyer tracks",
    text: "The order lands in your dashboard. With a courier partner connected, the buyer gets the tracking link automatically by email and, for physical products, in the DM. Returns and exchanges are handled there too.",
  },
];

const FAQS = [
  {
    q: "What is Checkout DM?",
    a: "Checkout DM is an Instagram DM checkout and automation tool for sellers. It connects to your Instagram professional account through Meta’s official login, and lets customers browse products, choose sizes, enter delivery details and pay inside the chat. You manage orders, returns and shipping from one dashboard.",
  },
  {
    q: "Who is Checkout DM for?",
    a: "Instagram sellers whose customers already message them to buy, such as clothing, jewellery and handmade sellers, and creators who sell digital products or courses.",
  },
  {
    q: "Do I need a website to use Checkout DM?",
    a: "No. The catalog and checkout run inside Instagram DMs. From the Creator plan you also get a shareable Storefront link you can post on WhatsApp, Telegram, YouTube, any website or any chat app.",
  },
  {
    q: "Do my customers need an Instagram account?",
    a: "Not always. You need an Instagram professional account to use Checkout DM, and the chat automation runs in Instagram DMs. From the Creator plan, your Storefront link can be shared anywhere on the internet, and buyers can order from it without Instagram.",
  },
  {
    q: "How much does Checkout DM cost?",
    a: "There is a Free plan at ₹0 with 5 products and 15 unique customer conversations a month. Paid plans run from ₹499 to ₹6,499 a month, with no commission or transaction fee on any plan.",
  },
  {
    q: "Is Checkout DM safe for my Instagram account?",
    a: "It connects through Meta’s official Instagram login, so your password is never shared, and you can remove its access from your Instagram settings at any time.",
  },
];

export default function Features() {
  useEffect(() => {
    markPrerenderReady();
  }, []);

  const url = `${SITE_URL}/features`;
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Checkout DM features",
      description:
        "What Checkout DM does for Instagram sellers: DM automation, comment-to-DM, Story replies, catalog and checkout, and order management.",
      url,
      isPartOf: { "@type": "WebSite", name: "Checkout DM", url: SITE_URL },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Features", item: url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  const track = (destination, location) =>
    trackEvent("feature_cta_click", { feature: "hub", cta_location: location, destination });

  return (
    <>
      <SEO
        title="Checkout DM Features: Instagram DM Checkout and Automation"
        description="See what Checkout DM does: DM automation, comment-to-DM, Story replies, product catalog and checkout, and order management for Instagram sellers."
        path="/features"
        structuredData={schema}
      />
      <Navbar />

      <header className="px-6 pb-10 pt-12 md:px-12 md:pt-20">
        <div className="mx-auto max-w-5xl">
          <span className="inline-block rounded-full bg-lilac px-3 py-1 text-xs font-bold text-ink">
            Features
          </span>
          <h1
            data-testid="features-title"
            className="mt-5 max-w-4xl text-balance font-display text-4xl leading-[0.98] tracking-tighter md:text-6xl"
          >
            Checkout DM turns Instagram DMs into a <span className="text-sunset">checkout</span>.
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink/70">
            Checkout DM is an Instagram DM checkout and automation tool for sellers. Customers browse
            your products, choose a size, enter their delivery details and pay inside the chat. You
            manage orders, returns and shipping from one dashboard, with no website needed and no
            commission on your sales. From the Creator plan, you can also share a Storefront link
            anywhere on the internet, and your buyers do not need Instagram to use it.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <Link
              to="/login"
              onClick={() => track("/login", "hero")}
              className="cta-glow rounded-full bg-sunset-gradient px-6 py-3 text-sm font-semibold text-white"
            >
              Connect Instagram
            </Link>
            <Link
              to="/pricing"
              onClick={() => track("/pricing", "hero")}
              className="text-sm font-semibold text-ink/80 hover:text-hotpink"
            >
              See pricing
            </Link>
          </div>
        </div>
      </header>

      <main className="px-6 pb-24 md:px-12">
        <div className="mx-auto max-w-5xl">
          <section aria-labelledby="how-a-sale-happens">
            <h2 id="how-a-sale-happens" className="font-display text-3xl leading-tight tracking-tight md:text-4xl">
              How a sale happens
            </h2>
            <ol className="mt-6 grid gap-3 md:grid-cols-5">
              {STEPS.map((s, i) => (
                <li key={s.title} className="rounded-2xl border border-ink/10 bg-white p-5">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-ink font-display text-sm font-black text-cream">
                    {i + 1}
                  </span>
                  <p className="mt-3 font-display text-[17px] font-bold leading-snug text-ink">{s.title}</p>
                  <p className="mt-1.5 text-[14px] leading-relaxed text-ink/65">{s.text}</p>
                </li>
              ))}
            </ol>
          </section>

          <section className="mt-16" aria-labelledby="what-it-does">
            <h2 id="what-it-does" className="font-display text-3xl leading-tight tracking-tight md:text-4xl">
              What Checkout DM does
            </h2>
            <ul className="mt-6 grid gap-4 md:grid-cols-2">
              {FEATURES.map((f) => (
                <li key={f.slug}>
                  <Link
                    to={f.path}
                    onClick={() => track(f.path, "feature_card")}
                    className="group flex h-full flex-col rounded-3xl border border-ink/10 bg-white p-6 transition-colors hover:border-hotpink/50"
                  >
                    <span className="flex items-center justify-between gap-3 font-display text-xl font-bold leading-snug text-ink">
                      {f.eyebrow}
                      <ArrowUpRight size={20} className="flex-none text-ink/40 transition-colors group-hover:text-hotpink" aria-hidden="true" />
                    </span>
                    <span className="mt-2 text-[15px] leading-relaxed text-ink/65">{f.description}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-16 grid gap-4 md:grid-cols-2" aria-label="Who it is for">
            <div className="rounded-3xl bg-mint/45 p-7">
              <h2 className="font-display text-2xl leading-tight tracking-tight">Made for</h2>
              <ul className="mt-4 space-y-2 text-[16px] leading-relaxed text-ink/80">
                <li>Instagram sellers whose customers already start in DMs.</li>
                <li>Clothing, jewellery, beauty and handmade sellers who ship physical products.</li>
                <li>Creators who sell digital files, courses or templates.</li>
                <li>Sellers who take payment by UPI today and want fewer manual steps.</li>
              </ul>
            </div>
            <div className="rounded-3xl bg-peach/30 p-7">
              <h2 className="font-display text-2xl leading-tight tracking-tight">Not the right fit if</h2>
              <ul className="mt-4 space-y-2 text-[16px] leading-relaxed text-ink/80">
                <li>You need chat automation on WhatsApp, TikTok or SMS. Checkout DM’s chat automation runs on Instagram, although your Storefront link can be shared anywhere.</li>
                <li>You want a fully custom-designed website. Checkout DM is not a website builder; it gives you a Storefront page.</li>
                <li>You only want to send replies or collect leads without selling. Checkout DM does collect the buyer’s email on paid orders, but it is built around selling.</li>
              </ul>
              <p className="mt-4 text-[15px] text-ink/70">
                Compare other tools honestly on our <Link to="/compare" className="font-semibold text-hotpink underline underline-offset-4">comparison page</Link>.
              </p>
            </div>
          </section>

          <section className="mt-16" aria-labelledby="pricing-summary">
            <h2 id="pricing-summary" className="font-display text-3xl leading-tight tracking-tight md:text-4xl">
              Simple pricing, no commission
            </h2>
            <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink/70">
              Start free with 5 products and 15 unique customer conversations a month. Paid plans run
              from ₹499 to ₹6,499 a month. Checkout DM takes no commission or transaction fee on any
              plan, and payments go straight to your own account.
            </p>
            <Link
              to="/pricing"
              onClick={() => track("/pricing", "pricing_section")}
              className="mt-5 inline-flex items-center gap-2 font-semibold text-hotpink hover:underline"
            >
              See every plan <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
          </section>

          <section className="mt-16" aria-labelledby="faq">
            <h2 id="faq" className="font-display text-3xl leading-tight tracking-tight md:text-4xl">
              Questions people ask first
            </h2>
            <div className="mt-6 divide-y divide-ink/10 rounded-3xl border border-ink/10 bg-white">
              {FAQS.map((f) => (
                <details key={f.q} className="group px-6 py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-lg font-bold text-ink">
                    {f.q}
                    <span aria-hidden="true" className="text-2xl leading-none text-hotpink transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 text-[16px] leading-relaxed text-ink/70">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          <aside className="mt-16 rounded-[2rem] bg-ink p-8 text-cream md:p-10">
            <h2 className="font-display text-3xl leading-[1.05] tracking-tight md:text-4xl">
              Turn your DMs into checkouts.
            </h2>
            <p className="mt-3 max-w-md text-[16px] leading-relaxed text-cream/70">
              Connect your Instagram account and set up your catalog in minutes.
            </p>
            <Link
              to="/login"
              onClick={() => track("/login", "page_end")}
              className="cta-glow mt-6 inline-block rounded-full bg-sunset-gradient px-6 py-3 text-sm font-semibold text-white"
            >
              Connect Instagram
            </Link>
          </aside>
        </div>
      </main>

      <Footer />
    </>
  );
}
